import moment from 'moment';
import { differenceInMinutes } from 'date-fns';
moment.locale('pt-br');

export const NORMALIZED_MIN_PATTERN_SLASHED_WITH_TIME = 'DD/MM/YYYY HH:mm:ss';
export const NORMALIZED_MIN_PATTERN_SLASHED = 'DD/MM/YYYY';
export const NORMALIZED_MIN_PATTERN_DASHED = 'YYYY-MM-DD';
export const NORMALIZED_PATTERN = 'YYYYMMDDHHmm';
export const NORMALIZED_ENTIRE_PATTERN = 'DD/MM/YYYY HH:mm';
export const NORMALIZED_TIME_PATTERN = 'YYYY[/]MM[/]DD HH[:]mm';
export const NORMALIZED_FULL_PATTERN = 'ddd MMM D YYYY HH:mm:ss ZZ';
export const NORMALIZED_GMT_TIME_ZONE = 'D[/]MM[/]YYYY HH:mm:ss ZZ';
export const STANDARD_OLD_TIME_STAMP = '200001010000';

export const PERIOD_LABEL = {
  TODAY: {
    KEY: 'TODAY',
    LABEL: 'Hoje',
    PREVIOUS_PERIOD_LABEL: 'ontem',
  },
  YESTERDAY: {
    KEY: 'YESTERDAY',
    LABEL: 'Ontem',
    PREVIOUS_PERIOD_LABEL: 'anteontem',
  },
  LAST_24_HOURS: {
    KEY: 'LAST_24_HOURS',
    LABEL: 'Últimas 24 horas',
    PREVIOUS_PERIOD_LABEL: 'nas 24 horas anteriores',
  },
  CURRENT_WEEK: {
    KEY: 'CURRENT_WEEK',
    LABEL: 'Semana atual',
    PREVIOUS_PERIOD_LABEL: 'na semana passada',
  },
  CURRENT_MOUTH: {
    KEY: 'CURRENT_MOUTH',
    LABEL: 'Mês atual',
    PREVIOUS_PERIOD_LABEL: 'no mês passado',
  },
  LAST_MOUTH: {
    KEY: 'LAST_MOUTH',
    LABEL: 'Mês passado',
    PREVIOUS_PERIOD_LABEL: 'no mês retrasado',
  },
  LAST_7_DAYS: {
    KEY: 'LAST_7_DAYS',
    LABEL: 'Últimos 7 dias',
    PREVIOUS_PERIOD_LABEL: 'nos 7 dias anteriores',
  },
  LAST_30_DAYS: {
    KEY: 'LAST_30_DAYS',
    LABEL: 'Últimos 30 dias',
    PREVIOUS_PERIOD_LABEL: 'nos 30 dias anteriores',
  },
  CUSTOM: {
    KEY: 'CUSTOM',
    LABEL: 'Customizado',
    PREVIOUS_PERIOD_LABEL: 'no mesmo intervalo anterior',
  },
};

/**
 * Process a date to a humanized form
 * @param timeInMs: time in milliseconds
 * @returns {string}: A humanized date (ex: 2 months ago)
 */
export function humanizeDate(timeInMs) {
  return moment.duration(moment(timeInMs, 'x') - moment()).humanize(true);
}

/**
 * Format the date with seconds
 * @param timeInMs: time in milliseconds
 * @returns {string}: A humanized date
 */
export function fullDate(timeInMs) {
  return moment(timeInMs, 'x').format(NORMALIZED_MIN_PATTERN_SLASHED_WITH_TIME);
}

/**
 * Format time from milliseconds to 'HHhMM'
 * @param time: time in milliseconds
 * @returns {string}: formatted date
 */
export function msToTime(time) {
  const ms = time % 1000;
  let s = (time - ms) / 1000;
  const secs = s % 60;
  s = (s - secs) / 60;
  const mins = s % 60;
  const hrs = (s - mins) / 60;

  const options = { minimumIntegerDigits: 2 };

  return `${hrs.toLocaleString('pt-BR', options)}h${mins.toLocaleString('pt-BR', options)}`;
}

/**
 * Format time from milliseconds to 'MMminSSs' (ex: 04min20s) or, when the duration is an
 * hour or longer, to 'HHhMMmin' (ex: 01h05min)
 * @param time: time in milliseconds
 * @returns {string}: formatted date
 */
export function msToMinutesAndSeconds(time) {
  const ms = time % 1000;
  const totalSeconds = (time - ms) / 1000;
  const secs = totalSeconds % 60;
  const totalMinutes = (totalSeconds - secs) / 60;

  const options = { minimumIntegerDigits: 2, useGrouping: false };

  if (totalMinutes >= 60) {
    const mins = totalMinutes % 60;
    const hrs = (totalMinutes - mins) / 60;

    return `${hrs.toLocaleString('pt-BR', options)}h${mins.toLocaleString('pt-BR', options)}min`;
  }

  return `${totalMinutes.toLocaleString('pt-BR', options)}min${secs.toLocaleString('pt-BR', options)}s`;
}

/**
 * Return the difference between two moments object
 * @param start: moment object
 * @param end: moment object
 * @param time: DATE_TIME @utils/constants.js
 * @returns {number}: the difference
 */
export function getDiffDates(start, end, time) {
  return end.diff(start, time);
}

/**
 * Return the difference between dates interval
 * @param rangeDate: String
 * @param time: DATE_TIME @utils/constants.js
 * @returns {number}: the difference
 */
export function getDiffInRangeDates(rangeDate, time) {
  const [start, end] = rangeDate
    .split(':')
    .map(date => moment(date, NORMALIZED_PATTERN));

  return getDiffDates(start, end, time);
}

/**
 * Format date in milliseconds
 * @param dateISO: date in ISO format
 * @returns {string}: milliseconds
 */
export function dateInMilliseconds(dateISO) {
  return moment(dateISO, NORMALIZED_MIN_PATTERN_DASHED).format('x');
}

/**
 * Convert time to local UTC
 * @param date: date object
 * @returns {Date}: miliseconds
 */
export function setDateToUTC(date) {
  const selected = new Date(date);
  const minutesToMillisec = 60000;
  return new Date(selected.getTime() + (selected.getTimezoneOffset() * minutesToMillisec));
}

/**
 * Convert time to string in YYYYMMDDHHMM string and add 3 hours so the time that is set in War Room matchs 0:00
 * @param selected: date object
 * @returns {string}: YYMMDDHHMM
 */
export function setDate(selected) {
  const date = setDateToUTC(selected);
  const selectedDate = String(date.getFullYear() + (String(date.getMonth() + 1).padStart(2, '0')) + (String(date.getDate()).padStart(2, '0')) + String('0300'));
  return selectedDate;
}

/**
 * Convert time to string in DD/MM/YYYY HH:MM in brazilian format
 * @param selected: date object
 * @returns {string}: DD/MM/YYYY HH:MM
 */
export function convertTimeToBrazilianFormatDate(date) {
  const formattedDate = new Date(date);

  const minutes = formattedDate.getMinutes().toString().padStart(2, '0');
  const hours = formattedDate.getHours().toString().padStart(2, '0');
  const day = formattedDate.getDate().toString().padStart(2, '0');
  const month = (formattedDate.getMonth() + 1).toString().padStart(2, '0');
  const year = formattedDate.getFullYear().toString();

  return `${day}/${month}/${year} ${hours}:${minutes}`;
}

/**
 * Convert the current date and time to string in YYMMDDHHmm format
 * @returns {string}: YYMMDD2359
 */
export function getTodayInNormalizedPattern() {
  const options = {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    timeZone: 'America/Sao_Paulo',
  };

  const currentDate = new Date();
  currentDate.setHours(23, 59);

  const formattedDate = currentDate.toLocaleString('en-US', options).replace(/[/,\s:]/gu, '');

  const rearrangedDate = formattedDate.slice(4, 8) + formattedDate.slice(0, 2) + formattedDate.slice(2, 4) + formattedDate.slice(8);

  return rearrangedDate;
}

/**
 * Calculates time from a date to the current moment.
 * @param postedAt: Date
 * @returns {string}: Time in hours or minutes
 */

export function calculateTimeSincePosted(postedAt, currentDate = new Date()) {
  const postedDate = new Date(postedAt);

  const minutesDifference = differenceInMinutes(currentDate, postedDate);
  const preciseHours = (minutesDifference / 60).toFixed(2);
  return {
    minutesDifference,
    preciseHours: parseFloat(preciseHours),
  };
}

